import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Coins } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { createCheckout, creditMock, listPaymentsHistory } from "../lib/billing";

export const RECHARGE_HREF = "/billing"; // fallback — checkout modal is primary
const FREE_DAILY_LIMIT = 3;
const POLL_MS = 10_000;
const CACHE_KEY = "nxa_wallet_cache";
const CACHE_TTL_MS = 5 * 60 * 1000;

function loadCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (Date.now() - (parsed.updated_at || 0) > CACHE_TTL_MS) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function saveCache(payload) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ ...payload, updated_at: Date.now() }),
    );
  } catch (_) {
    /* ignore */
  }
}

function clearCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (_) {
    /* ignore */
  }
}

/**
 * useWallet — single source of truth for wallet state in the UI.
 *
 *  - Reads from AuthContext's user (server truth).
 *  - Hydrates from localStorage cache (CACHE_KEY) to avoid "0 NXT" flicker.
 *  - Polls GET /users/me every 10s while authed AND tab visible.
 *  - Auto-detects positive balance deltas and fires a gold toast
 *    "NEXATOKENS RECEIVED..." (2s debounce vs duplicates).
 *  - Exposes purchaseTokens/subscribePro/downgradeFree/creditMockNxt methods.
 */
export function useWallet() {
  const { t } = useTranslation();
  const { user, refresh, isAuthed, isLoading, logout: authLogout } = useAuth();

  const [cached] = useState(() => loadCache());
  const [lastUpdated, setLastUpdated] = useState(() => Date.now());
  const prevBalanceRef = useRef(null);
  const lastToastRef = useRef({ delta: 0, at: 0 });

  // When real user data arrives, persist to cache and track last update time
  useEffect(() => {
    if (!user || user === false) return;
    saveCache({
      user_id: user.id,
      balance: user.nxt_balance,
      plan: user.plan,
      daily_quick_count: user.daily_quick_count,
    });
    setLastUpdated(Date.now());
  }, [user?.id, user?.nxt_balance, user?.plan, user?.daily_quick_count]);

  // Detect positive balance delta → toast
  useEffect(() => {
    if (!user || user === false) return;
    const prev = prevBalanceRef.current;
    const now = user.nxt_balance;
    if (prev !== null && now > prev) {
      const delta = now - prev;
      const since = Date.now() - lastToastRef.current.at;
      const dup = delta === lastToastRef.current.delta && since < 2000;
      if (!dup) {
        toast.success(t("wallet.credit.received", { delta }), {
          icon: <Coins className="h-4 w-4 text-[#D4AF37]" />,
          duration: 5000,
          style: {
            border: "1px solid rgba(212,175,55,0.55)",
            background: "rgba(12,12,14,0.96)",
            color: "#F4D77A",
            boxShadow: "0 10px 30px -10px rgba(212,175,55,0.35)",
          },
          className: "nxa-credit-toast",
        });
        lastToastRef.current = { delta, at: Date.now() };
      }
    }
    prevBalanceRef.current = now;
  }, [user?.nxt_balance, t]);

  // Clear cache on logout
  const logout = useCallback(() => {
    clearCache();
    authLogout();
  }, [authLogout]);

  // Polling every POLL_MS when authed + tab visible
  useEffect(() => {
    if (!isAuthed) return;
    let id = null;
    const tick = async () => {
      await refresh();
    };
    const start = () => {
      if (id == null) id = setInterval(tick, POLL_MS);
    };
    const stop = () => {
      if (id != null) {
        clearInterval(id);
        id = null;
      }
    };
    const onVis = () => {
      if (document.visibilityState === "visible") start();
      else stop();
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      stop();
    };
  }, [isAuthed, refresh]);

  // Use server user if available, otherwise cache (for flicker-free first paint)
  const effective = user && user !== false ? user : cached;
  const plan = effective?.plan ?? "free";
  const isPro = plan === "pro";
  const balance = effective?.nxt_balance ?? effective?.balance ?? 0;
  const dailyQuickCount =
    effective?.daily_quick_count ?? 0;
  const remaining = isPro ? null : Math.max(0, FREE_DAILY_LIMIT - dailyQuickCount);
  const hasDeepQuota = balance >= 5;

  // --- Billing actions ----------------------------------------------------
  //
  // Phase 9: Stripe Checkout hosted flow. The client sends only the target key
  // and its origin URL; the server returns { session_id, url } and the caller
  // does `window.location.href = url` to redirect to stripe.com. Confirmation
  // happens on redirect-back via /billing?success=true&session_id=... — see
  // pages/Billing.jsx for the post-redirect flow.

  const startCheckout = useCallback(
    async (target, originUrl) => {
      const origin = originUrl || (typeof window !== "undefined" ? window.location.origin : "");
      return await createCheckout({ target, originUrl: origin });
    },
    [],
  );

  const purchaseTokens = useCallback(
    async (packageId, originUrl) => startCheckout(packageId, originUrl),
    [startCheckout],
  );

  const subscribePro = useCallback(
    async (originUrl) => startCheckout("pro_monthly", originUrl),
    [startCheckout],
  );

  const downgradeFree = useCallback(async () => {
    // Real Stripe subscriptions aren't wired (see PRD backlog); the Pro tier
    // is a one-time upgrade for MVP. Downgrade just flips the flag locally
    // via the utility endpoint — a real recurring subscription cancel would
    // hit stripe.Subscription.delete instead.
    // For now, we no-op with a helpful message in the UI.
    return { ok: false, status: 501, message: "downgrade_not_implemented" };
  }, []);

  const creditMockNxt = useCallback(
    async (nxt_amount, source = "manual_grant") => {
      const res = await creditMock({ nxt_amount, source });
      if (res.ok) await refresh();
      return res;
    },
    [refresh],
  );

  const fetchHistory = useCallback(async (page = 1, pageSize = 20) => {
    return await listPaymentsHistory(page, pageSize);
  }, []);

  const result = useMemo(
    () => ({
      user: effective,
      isAuthed,
      isLoading: isLoading && !cached,
      plan,
      isPro,
      balance,
      dailyQuickCount,
      remaining,
      hasDeepQuota,
      rechargeHref: RECHARGE_HREF,
      lastUpdated,
      refresh,
      logout,
      purchaseTokens,
      subscribePro,
      downgradeFree,
      creditMockNxt,
      fetchHistory,
    }),
    [
      effective,
      isAuthed,
      isLoading,
      cached,
      plan,
      isPro,
      balance,
      dailyQuickCount,
      remaining,
      hasDeepQuota,
      lastUpdated,
      refresh,
      logout,
      purchaseTokens,
      subscribePro,
      downgradeFree,
      creditMockNxt,
      fetchHistory,
    ],
  );

  return result;
}
